/**
 * The text of a PDF as rows of cells (docs/18-vorratsbuch.md, Eingänge): what pdf.js gives per page
 * are pieces of text with a position, in no reliable order, often a word split at an umlaut
 * („M“ „ö“ „hren“). Here they become rows (same height on the page) of cells (pieces that touch).
 * Reading a PDF itself is the device's job (pdf.js); the core only sees the pieces, so the
 * converters can be tested without a PDF.
 */

/** A piece of text as pdf.js places it: x and y in points from the lower left, width in points. */
export interface PdfItem {
  page: number;
  x: number;
  y: number;
  w: number;
  str: string;
}

export interface PdfCell {
  x: number;
  /** Right edge. */
  end: number;
  text: string;
}

export interface PdfRow {
  page: number;
  y: number;
  cells: PdfCell[];
  /** The cells joined by a space, for patterns over the whole row. */
  text: string;
}

/** Pieces on one row may sit a point or two apart in height (a number set lower than its unit). */
const ROW_TOLERANCE = 2.5;
/** Pieces closer than this belong to one cell; a space in the PDF is usually 3 points or more. */
const TOUCH = 1.5;

/** Rows from top to bottom, page by page; cells from left to right. */
export function pdfRows(items: readonly PdfItem[]): PdfRow[] {
  const rows: PdfRow[] = [];
  const pages = [...new Set(items.map((i) => i.page))].sort((a, b) => a - b);
  for (const page of pages) {
    const onPage = items.filter((i) => i.page === page && i.str.trim() !== "").sort((a, b) => b.y - a.y || a.x - b.x);
    const groups: PdfItem[][] = [];
    for (const it of onPage) {
      const last = groups[groups.length - 1];
      if (last && Math.abs(last[0]!.y - it.y) <= ROW_TOLERANCE) last.push(it);
      else groups.push([it]);
    }
    for (const group of groups) {
      const cells: PdfCell[] = [];
      for (const it of group.sort((a, b) => a.x - b.x)) {
        const prev = cells[cells.length - 1];
        if (prev && it.x - prev.end < TOUCH) {
          prev.text += it.str;
          prev.end = Math.max(prev.end, it.x + it.w);
        } else cells.push({ x: it.x, end: it.x + it.w, text: it.str });
      }
      for (const c of cells) c.text = c.text.replace(/\s+/g, " ").trim();
      rows.push({ page, y: group[0]!.y, cells, text: cells.map((c) => c.text).join(" ") });
    }
  }
  return rows;
}

/** „1.234,56“ or „0,704“ as a number; null if it is none. */
export function germanNumber(text: string): number | null {
  const t = text.trim();
  if (!/^-?\d{1,3}(\.\d{3})*(,\d+)?$|^-?\d+(,\d+)?$/.test(t)) return null;
  return Number(t.replace(/\./g, "").replace(",", "."));
}

/** „7,30“ (euros) as cents. */
export function cents(text: string): number | null {
  const n = germanNumber(text);
  return n === null ? null : Math.round(n * 100);
}

/** „20.2.2026“ as „2026-02-20“. */
export function germanDate(text: string): string | null {
  const m = /(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(text);
  if (!m) return null;
  return `${m[3]}-${m[2]!.padStart(2, "0")}-${m[1]!.padStart(2, "0")}`;
}
